import {
  Address,
  DraftEmailType,
  EmailProvider,
  EmailRequestType,
  MessagingPlatform,
  UnifiedSendEmailRequest,
  UnifiedSendEmailResponse,
} from '@this+that/models';
import log from 'loglevel';

import { DraftConflictResult } from './draftConflict';
import { DraftAccount, resolveDraftAccount } from './resolveEmailAccount';
import {
  DraftVersionConflictError,
  sendEmails,
} from '../../../services/unifiedEmailAction.service';
import { useIntegrationsStore } from '../../../store';
import {
  EMPTY_CONTENT_PATTERNS,
  extractUserInputFromTemplate,
  hasTemplateContent,
  isContentEmpty,
} from '../../../utils/editorContentUtils';
import { getAddressesByType } from '../../../utils/getAddressesByType';
import { FileAttachment, formatAttachmentsForApi } from '../../index';

export type SavedDraftResult = UnifiedSendEmailResponse & {
  platform: MessagingPlatform;
  draftAccount?: DraftAccount;
};

export const useSaveDraft = () => {
  const { currentIntegrationAccount, integrationAccounts } =
    useIntegrationsStore();

  const saveDraft = async (
    content?: string,
    subject?: string,
    recipients: Address[] = [],
    attachments?: FileAttachment[],
    draftId?: string,
    fromEmail?: string,
    messageId?: string,
    draftType?: DraftEmailType,
    modifiedQuotedContent?: string,
    expectedDraftVersion?: string,
    pinnedAccount?: DraftAccount,
  ): Promise<SavedDraftResult | DraftConflictResult | null> => {
    try {
      if (!currentIntegrationAccount) {
        log.error('No integration mail found');
        return null;
      }

      const {
        to: toAddresses = [],
        cc: ccAddresses = [],
        bcc: bccAddresses = [],
      } = getAddressesByType(recipients);

      const draftAccount =
        draftId && pinnedAccount
          ? pinnedAccount
          : resolveDraftAccount(
              fromEmail,
              currentIntegrationAccount,
              integrationAccounts,
            );

      if (!draftAccount) {
        return null;
      }

      const { accountId: fromEmailAccountId, platform } = draftAccount;

      let bodyContent = content;
      if (
        content &&
        hasTemplateContent(content) &&
        (draftType === DraftEmailType.REPLY ||
          draftType === DraftEmailType.FORWARD)
      ) {
        bodyContent = extractUserInputFromTemplate(content);
      }

      const isUpdatingExistingDraft = Boolean(draftId);
      const isBodyEmpty = !bodyContent || isContentEmpty(bodyContent);

      const payload: UnifiedSendEmailRequest = {
        providerAccount: {
          provider: platform as EmailProvider,
          accountId: fromEmailAccountId,
        },
        reqType: EmailRequestType.DRAFT,
        body: isBodyEmpty
          ? isUpdatingExistingDraft
            ? // server treats a falsy body as "keep existing draft body", so send a visually-blank non-empty body to actually clear it
              EMPTY_CONTENT_PATTERNS.PARAGRAPH_BR
            : undefined
          : bodyContent,
        subject: subject === '' ? undefined : subject,
        // The compositor is the single owner of the reviewed recipient lists here — an
        // empty array must reach the server as "clear this field", not be collapsed to
        // undefined ("keep whatever the draft already has").
        toAddresses,
        ccAddresses,
        bccAddresses,
        attachments:
          attachments && attachments.length > 0
            ? formatAttachmentsForApi(attachments)
            : undefined,
        draftId,
        messageId,
        draftType,
        modifiedQuotedContent,
        expectedDraftVersion,
      };

      const response = await sendEmails(payload);
      return {
        ...(response.message as UnifiedSendEmailResponse),
        platform,
        draftAccount,
      };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        log.debug('Save draft request was aborted');
        return null;
      }
      if (error instanceof DraftVersionConflictError) {
        log.warn('Draft version conflict while saving:', error);
        return {
          isDraftConflict: true,
          currentDraftVersion: error.currentDraftVersion,
        };
      }
      log.warn('Failed to save draft silently:', error);
      return null;
    }
  };

  return {
    saveDraft,
  };
};
